import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { HistoryMode, RouteQueryOptions, serializeUrlQueryParameters } from './use-url-query-parameters-sync';

type RouteState<TState extends object> = {
  from?: string;
} & TState;

type RouteDestination<TQuery extends object> = {
  path?: string;
  query?: Partial<TQuery>;
  fragment?: string;
};

type NavigationOptions<TState extends object> = {
  history?: HistoryMode;
  state?: RouteState<TState>;
};

type RouteNavigateAction<TQuery extends object, TState extends object> = (
  destination: string | RouteDestination<TQuery>,
  options?: NavigationOptions<TState>
) => void;

export type RouteNavigate<TQuery extends object, TState extends object> = RouteNavigateAction<
  TQuery,
  TState
> &
  Readonly<{
    visit: RouteNavigateAction<TQuery, TState>;
    resume: () => void;
  }>;

export function useUrlNavigation<TQuery extends object, TState extends object>(
  queryOptions: RouteQueryOptions<TQuery> = {}
): RouteNavigate<TQuery, TState> {
  const location = useLocation();
  const navigate = useNavigate();

  const { format, empty = 'omit' } = queryOptions;

  const navigateTo = useCallback<RouteNavigateAction<TQuery, TState>>(
    (destination, options = {}) => {
      const navigationOptions = {
        replace: options.history === 'replace',
        ...(Object.hasOwn(options, 'state') ? { state: options.state } : {}),
      };

      if (typeof destination === 'string') {
        void navigate(destination, navigationOptions);
        return;
      }

      const serializedQuery =
        destination.query === undefined
          ? location.search
          : serializeUrlQueryParameters(destination.query, format, empty).params.toString();

      const fragment =
        destination.fragment === undefined ? location.hash : destination.fragment.replace(/^#+/, '');

      void navigate(
        {
          pathname: destination.path ?? location.pathname,
          search: serializedQuery
            ? serializedQuery.startsWith('?')
              ? serializedQuery
              : `?${serializedQuery}`
            : '',
          hash: fragment ? (fragment.startsWith('#') ? fragment : `#${fragment}`) : '',
        },
        navigationOptions
      );
    },
    [empty, format, location.hash, location.pathname, location.search, navigate]
  );

  const visit = useCallback<RouteNavigateAction<TQuery, TState>>(
    (destination, options = {}) => {
      const state = {
        ...options.state,
        from: `${location.pathname}${location.search}${location.hash}`,
      } as RouteState<TState>;

      navigateTo(destination, { ...options, state });
    },
    [location.hash, location.pathname, location.search, navigateTo]
  );

  const resume = useCallback(() => {
    const from = (location.state as { from?: unknown } | null)?.from;

    if (typeof from === 'string' && from) {
      navigateTo(from);
    }
  }, [location.state, navigateTo]);

  return useMemo(() => {
    const routeNavigate: RouteNavigateAction<TQuery, TState> = (destination, options) => {
      navigateTo(destination, options);
    };

    return Object.assign(routeNavigate, { visit, resume });
  }, [navigateTo, resume, visit]);
}
